import React from 'react';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Paper,
    InputAdornment,
    IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { injectIntl } from 'react-intl';

const AUTH_TYPES = [
    { value: 'NONE', labelId: 'webhook.auth.none' },
    { value: 'BASIC', labelId: 'webhook.auth.basic' },
    { value: 'BEARER', labelId: 'webhook.auth.bearer' },
    { value: 'API_KEY', labelId: 'webhook.auth.apikey' },
    { value: 'OAUTH2_CLIENT_CREDENTIALS', labelId: 'webhook.auth.oauth2' }
];

function WebhookAuthConfig(props) {
    const { authType, authConfig, onChange, intl } = props;
    const [showPassword, setShowPassword] = React.useState(false);
    const [showToken, setShowToken] = React.useState(false);
    const [showClientSecret, setShowClientSecret] = React.useState(false);

    const handleAuthTypeChange = (event) => {
        const newAuthType = event.target.value;
        onChange('authType', newAuthType);
        // Reset auth config when type changes
        onChange('authConfig', {});
    };

    const handleConfigChange = (field, value) => {
        const newConfig = { ...authConfig, [field]: value };
        onChange('authConfig', newConfig);
    };

    const renderAuthFields = () => {
        switch (authType) {
            case 'BASIC':
                return (
                    <>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label={intl.formatMessage({ id: 'webhook.auth.username' })}
                                value={authConfig.username || ''}
                                onChange={(e) => handleConfigChange('username', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                type={showPassword ? 'text' : 'password'}
                                label={intl.formatMessage({ id: 'webhook.auth.password' })}
                                value={authConfig.password || ''}
                                onChange={(e) => handleConfigChange('password', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </>
                );

            case 'BEARER':
                return (
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            type={showToken ? 'text' : 'password'}
                            label={intl.formatMessage({ id: 'webhook.auth.token' })}
                            value={authConfig.token || ''}
                            onChange={(e) => handleConfigChange('token', e.target.value)}
                            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowToken(!showToken)}
                                            edge="end"
                                        >
                                            {showToken ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                );

            case 'API_KEY':
                return (
                    <>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label={intl.formatMessage({ id: 'webhook.auth.header.name' })}
                                value={authConfig.headerName || ''}
                                onChange={(e) => handleConfigChange('headerName', e.target.value)}
                                placeholder="X-API-Key"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                type={showToken ? 'text' : 'password'}
                                label={intl.formatMessage({ id: 'webhook.auth.api.key.value' })}
                                value={authConfig.apiKey || ''}
                                onChange={(e) => handleConfigChange('apiKey', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowToken(!showToken)}
                                                edge="end"
                                            >
                                                {showToken ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </>
                );

            case 'OAUTH2_CLIENT_CREDENTIALS':
                return (
                    <>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label={intl.formatMessage({ id: 'webhook.auth.client.id' })}
                                value={authConfig.clientId || ''}
                                onChange={(e) => handleConfigChange('clientId', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                type={showClientSecret ? 'text' : 'password'}
                                label={intl.formatMessage({ id: 'webhook.auth.client.secret' })}
                                value={authConfig.clientSecret || ''}
                                onChange={(e) => handleConfigChange('clientSecret', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowClientSecret(!showClientSecret)}
                                                edge="end"
                                            >
                                                {showClientSecret ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label={intl.formatMessage({ id: 'webhook.auth.token.url' })}
                                value={authConfig.tokenUrl || ''}
                                onChange={(e) => handleConfigChange('tokenUrl', e.target.value)}
                                placeholder="https://auth.example.com/oauth/token"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label={intl.formatMessage({ id: 'webhook.auth.scope' })}
                                value={authConfig.scope || ''}
                                onChange={(e) => handleConfigChange('scope', e.target.value)}
                                placeholder="read write"
                            />
                        </Grid>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <Paper variant="outlined" style={{ padding: 16 }}>
            <Typography variant="subtitle1" gutterBottom>
                {intl.formatMessage({ id: 'webhook.authentication' })}
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>{intl.formatMessage({ id: 'webhook.auth.type' })}</InputLabel>
                        <Select
                            value={authType}
                            onChange={handleAuthTypeChange}
                            label={intl.formatMessage({ id: 'webhook.auth.type' })}
                        >
                            {AUTH_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {intl.formatMessage({ id: type.labelId })}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {renderAuthFields()}
            </Grid>
        </Paper>
    );
}

export default injectIntl(WebhookAuthConfig);
